import {ILoesungshaeufigkeitenWerte, ISchueler_testsAPI, ITestheftAPI} from './Interfaces';
import {IDomaenenDB, ISchuelerDB} from './InterfacesDB';
import Tooltip from './Tooltip';
import TooltipBase from './TooltipBase';
import TooltipItem from './TooltipItem';

const BOOTSTRAP_ICON_SPRITE = '/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg';

interface IErgebnisstatus {
    kurzbezeichnung: string;
    bezeichnung: string;
    cssKlasse: string;
}

/** Zeigt am Lösungshäufigkeitsbalken die Einzelbewertungen einer Schülerin oder eines Schülers */
export default class TooltipSchuelerergebnisse extends TooltipBase {
    private readonly testIndex: ReadonlyMap<number, ISchueler_testsAPI>; // Zugriff auf Ergebnisse über die Schüler-Test-ID
    private readonly testheftIndex: ReadonlyMap<number, ITestheftAPI>; // Zugriff auf Items in Testheftreihenfolge
    private readonly schuelerIndex: ReadonlyMap<number, ISchuelerDB>; // Zugriff auf die angezeigte Schülernummer

    public constructor(testhefte: readonly ITestheftAPI[], tests: readonly ISchueler_testsAPI[], schueler: readonly ISchuelerDB[], private readonly domaenen: readonly IDomaenenDB[], private readonly tooltipItem: TooltipItem | null, private readonly projektweiche: string, private readonly gruppeId: number, private readonly aktualisiereKlarnamen: () => void) {
        super({
            id: 'tooltip-schuelerergebnisse',
            cssKlasse: 'tooltip-schuelerergebnisse',
            inhaltCssKlasse: 'tooltip-schuelerergebnisse-content',
            ariaLabel: 'Schülerergebnisse',
            triggerSelektor: '[data-tooltip-schuelerergebnisse-test-id][data-tooltip-schuelerergebnisse-kategorie]',
            triggerAttribute: ['data-tooltip-schuelerergebnisse-test-id', 'data-tooltip-schuelerergebnisse-kategorie']
        });

        this.testIndex = new Map(tests
            .filter((test): test is ISchueler_testsAPI & {schueler_test_id: number} => test.schueler_test_id !== undefined)
            .map(test => [test.schueler_test_id, test]));
        this.testheftIndex = new Map(testhefte.map(testheft => [testheft.testheft_id, testheft]));
        this.schuelerIndex = new Map(schueler
            .filter((eintrag): eintrag is ISchuelerDB & {schueler_id: number} => eintrag.schueler_id !== undefined)
            .map(eintrag => [eintrag.schueler_id, eintrag]));
    }

    /** Liest Test und Ergebnisbereich des Balkens und rendert die kompakte Ergebnisübersicht */
    protected renderTrigger(trigger: HTMLElement): boolean {
        const testIdRaw = trigger.dataset.tooltipSchuelerergebnisseTestId ?? '';
        const kategorie = trigger.dataset.tooltipSchuelerergebnisseKategorie?.trim() ?? '';
        const testId = Number(testIdRaw);
        if (testIdRaw.trim() === '' || kategorie === '' || Number.isInteger(testId) === false)
            return false;

        const test = this.testIndex.get(testId) ?? null;
        const testheft = (test?.testheft_id === undefined) ? null : this.testheftIndex.get(test.testheft_id) ?? null;
        const schueler = (test?.schueler_id === undefined) ? null : this.schuelerIndex.get(test.schueler_id) ?? null;
        const werte = this.getLoesungshaeufigkeiten(test, kategorie);
        if (test === null || testheft === null || schueler === null || werte === null)
            return false;

        const ergebnisseNachItem = new Map(test.ergebnisse.map(ergebnis => [ergebnis.item_id, ergebnis.ergebnis]));
        const items = testheft.module
            .flatMap(modul => modul.aufgaben
                .flatMap(aufgabe => aufgabe.items.map(item => ({aufgabe, item}))))
            .filter(eintrag => kategorie === 'insgesamt' || eintrag.item.domaene === kategorie);
        const zeigtMehrereInhaltsbereiche = new Set(items.map(eintrag => eintrag.item.domaene)).size > 1;
        const itemzeilen = items.map(({aufgabe, item}) => {
            const status = this.getErgebnisstatus(ergebnisseNachItem.get(item.item_id));
            const itemBezeichnung = this.tooltipItem?.getItemBezeichnung(testheft.testheft_id, item.item_id) ?? `Item ${item.item_id}`;
            const aufgabenname = aufgabe.bezeichnung.trim();
            let aufgabennameHtml = '';
            if (aufgabenname !== '') {
                aufgabennameHtml = (testheft.fach === 'ma')
                    ? Tooltip.content`<span class="tooltip-schuelerergebnisse-aufgabenname">${aufgabenname}</span>`
                    : Tooltip.content`<span class="tooltip-schuelerergebnisse-aufgabenname" data-tooltip-aufgabe-id="${aufgabe.aufgabe_id}" data-tooltip-testheft-id="${testheft.testheft_id}">${aufgabenname}</span>`;
            }
            const statusHtml = Tooltip.content`<li class="tooltip-schuelerergebnisse-item"><span class="tooltip-schuelerergebnisse-status ${status.cssKlasse}" aria-label="${status.bezeichnung}" title="${status.bezeichnung}">${status.kurzbezeichnung}</span><span class="tooltip-schuelerergebnisse-bezeichnungen">`;
            const itemnameHtml = Tooltip.content`<span class="tooltip-schuelerergebnisse-itemname" data-tooltip-item-id="${item.item_id}" data-tooltip-testheft-id="${testheft.testheft_id}">${itemBezeichnung}</span>`;
            const inhaltsbereichHtml = zeigtMehrereInhaltsbereiche ? this.renderInhaltsbereich(testheft.fach, item.domaene) : '';

            return `${statusHtml}<span class="tooltip-schuelerergebnisse-itemkopf">${itemnameHtml}${inhaltsbereichHtml}</span>${aufgabennameHtml}</span></li>`;
        });
        const schuelerNr = schueler.nr?.toString().padStart(3, '0') ?? '-';
        const bereich = (kategorie === 'insgesamt')
            ? 'Alle Inhaltsbereiche'
            : `Inhaltsbereich: ${this.domaenen.find(domaene => domaene.domaene === kategorie)?.bezeichnung ?? kategorie}`;
        const aufgabenebeneUrl = `/rueckmeldung-lh-aufgabenebene.php?fach=${testheft.fach}&gruppeid=${this.gruppeId}`;
        const titel = Tooltip.content`<strong class="tooltip-schuelerergebnisse-title"><span data-tresor-anzeige="schueler" data-nr="${schueler.nr}">Schüler:in ${schuelerNr}</span> - ${bereich}</strong>`;
        const kopf = Tooltip.content`
            <p class="tooltip-schuelerergebnisse-zusammenfassung">
                <span><span class="tooltip-schuelerergebnisse-status richtig" aria-hidden="true">R</span><strong>${werte.r}</strong> richtig <span class="tooltip-schuelerergebnisse-prozent">(${werte.rProz} %)</span></span>
                <span><span class="tooltip-schuelerergebnisse-status nicht-bearbeitet" aria-hidden="true">N</span><strong>${werte.n}</strong> nicht bearbeitet <span class="tooltip-schuelerergebnisse-prozent">(${werte.nProz} %)</span></span>
                <span><span class="tooltip-schuelerergebnisse-status falsch" aria-hidden="true">F</span><strong>${werte.f}</strong> falsch <span class="tooltip-schuelerergebnisse-prozent">(${werte.fProz} %)</span></span>
            </p>
        `;
        const footer = Tooltip.content`<footer class="tooltip-datenhinweis"><span class="tooltip-schuelerergebnisse-footer-navigation"><svg class="tooltip-schuelerergebnisse-footer-pfeil" aria-hidden="true" focusable="false"><use href="${BOOTSTRAP_ICON_SPRITE}#arrow-right"/></svg><a class="tooltip-schuelerergebnisse-aufgabenebene-link" href="${aufgabenebeneUrl}">Lösungshäufigkeiten auf Aufgabenebene</a></span></footer>`;

        this.content.innerHTML = `${titel}${kopf}<ul class="tooltip-schuelerergebnisse-liste">${itemzeilen.join('')}</ul>${footer}`;
        this.aktualisiereKlarnamen();
        return true;
    }

    /** Rendert bei gemischten Itemmengen das verlinkte Kürzel des jeweiligen Inhaltsbereichs */
    private renderInhaltsbereich(fach: string, domaenenKuerzel: string): string {
        const domaene = this.domaenen.find(eintrag => eintrag.domaene === domaenenKuerzel) ?? null;
        const kurzbezeichnung = domaene?.kurzbezeichnung?.trim() ?? '';
        if (kurzbezeichnung === '')
            return '';

        const kurzbeschreibungUrl = TooltipItem.getDomaenenKurzbeschreibungUrl(this.projektweiche, fach, domaenenKuerzel);
        if (kurzbeschreibungUrl === null)
            return Tooltip.content`<span class="tooltip-schuelerergebnisse-inhaltsbereich">${kurzbezeichnung}</span>`;

        const bezeichnung = domaene?.bezeichnung?.trim() ?? kurzbezeichnung;
        return Tooltip.content`<a class="tooltip-schuelerergebnisse-inhaltsbereich" href="${kurzbeschreibungUrl}" target="_blank" rel="noopener" aria-label="Inhaltsbereich ${bezeichnung}: Kurzbeschreibung als PDF öffnen">${kurzbezeichnung}</a>`;
    }

    /** Ermittelt die Lösungshäufigkeiten des Gesamtbalkens oder der gewählten Domäne */
    private getLoesungshaeufigkeiten(test: ISchueler_testsAPI | null, kategorie: string): ILoesungshaeufigkeitenWerte | null {
        if (test?.loesungshaeufigkeiten === null || test?.loesungshaeufigkeiten === undefined)
            return null;
        if (kategorie === 'insgesamt')
            return test.loesungshaeufigkeiten.insgesamt;

        return test.loesungshaeufigkeiten.domaenen.find(domaene => domaene.name === kategorie)?.werte ?? null;
    }

    /** Übersetzt den gespeicherten Ergebniswert in die Rückmeldekategorien R, N und F */
    private getErgebnisstatus(ergebnis: number | undefined): IErgebnisstatus {
        if (ergebnis === 1)
            return {kurzbezeichnung: 'R', bezeichnung: 'Richtig gelöst', cssKlasse: 'richtig'};
        if (ergebnis === -1)
            return {kurzbezeichnung: 'N', bezeichnung: 'Nicht bearbeitet', cssKlasse: 'nicht-bearbeitet'};
        if (ergebnis === 0)
            return {kurzbezeichnung: 'F', bezeichnung: 'Falsch gelöst', cssKlasse: 'falsch'};

        return {kurzbezeichnung: '?', bezeichnung: 'Kein Ergebnis verfügbar', cssKlasse: 'unbekannt'};
    }
}
